import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, ToggleButton, ToggleButtonGroup, TextField, MenuItem,
  IconButton, CircularProgress, Alert, Collapse, Divider,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import PageHeader from '../../components/PageHeader';
import StatCard from '../../components/StatCard';
import { getUsersAnalyticsTimeseriesApi } from '../../services/api';

// ─── Series colors (validated categorical slots — see dataviz skill) ───────
// green (slot 2) / yellow-gold (slot 4): worst-pair CVD ΔE 16.2 light / 6.9 dark,
// normal-vision ΔE 30.3 light / 23.3 dark. Gold sits below 3:1 contrast on a
// white surface, so it always ships with the legend, tooltip and table below —
// never color alone.
const GREEN = '#008300';
const GOLD = '#eda100';

const PLOT_HEIGHT = 260;
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ─── Date helpers (all bucketing is UTC-calendar-day, matching the API) ────

const pad2 = (n) => String(n).padStart(2, '0');
const toISODate = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`; // m is 0-11

const monthRange = (year, month) => {
  const start = toISODate(year, month, 1);
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const end = toISODate(year, month, lastDay);
  return { start, end };
};

const parseDayKey = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const fmtAxisDay = (key, withMonth) => parseDayKey(key).toLocaleDateString(
  'en-US',
  withMonth ? { month: 'short', day: 'numeric', timeZone: 'UTC' } : { day: 'numeric', timeZone: 'UTC' },
);

const fmtTooltipDate = (key) => parseDayKey(key).toLocaleDateString('en-US', {
  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
});

// ─── Y-axis "nice numbers" ───────────────────────────────────────────────

function niceNumber(value, round) {
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / 10 ** exponent;
  let niceFraction;
  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }
  return niceFraction * 10 ** exponent;
}

function computeScale(maxValue) {
  if (maxValue <= 0) return { niceMax: 4, ticks: [0, 1, 2, 3, 4] };
  const roughStep = niceNumber(maxValue / 4, true);
  const niceMax = Math.ceil(maxValue / roughStep) * roughStep;
  const ticks = [];
  for (let t = 0; t <= niceMax; t += roughStep) ticks.push(t);
  return { niceMax, ticks };
}

// ─── Chart sub-components ───────────────────────────────────────────────

function LegendDot({ color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
    </Box>
  );
}

function DayTooltip({ day, position }) {
  const rows = [
    { label: 'Gold', value: day.gold, color: GOLD },
    { label: 'Green', value: day.green, color: GREEN },
  ];
  return (
    <Box className="bar-tooltip" sx={{
      position: 'absolute', bottom: 'calc(100% + 10px)',
      ...(position === 'start' ? { left: 0 } : position === 'end' ? { right: 0 } : { left: '50%', transform: 'translateX(-50%)' }),
      bgcolor: '#1a1a19', color: '#fff', borderRadius: 1.5, px: 1.5, py: 1,
      width: 148, boxShadow: '0 8px 24px rgba(11,11,11,0.28)',
      opacity: 0, visibility: 'hidden', transition: 'opacity .12s ease',
      pointerEvents: 'none', zIndex: 20,
    }}>
      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.55)', mb: 0.75, fontWeight: 600 }}>
        {fmtTooltipDate(day.date)}
      </Typography>
      {rows.map((row) => (
        <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, py: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 3, borderRadius: 2, bgcolor: row.color, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{row.label}</Typography>
          </Box>
          <Typography variant="caption" fontWeight={700}>{row.value}</Typography>
        </Box>
      ))}
      <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.16)' }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Total</Typography>
        <Typography variant="caption" fontWeight={700}>{day.total}</Typography>
      </Box>
    </Box>
  );
}

function SubscriberBarChart({ days }) {
  const maxTotal = Math.max(0, ...days.map((d) => d.total));
  const { niceMax, ticks } = computeScale(maxTotal);
  const hasData = maxTotal > 0;
  const labelInterval = Math.max(1, Math.ceil(days.length / 15));
  let lastLabeledMonth = null;

  return (
    <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #e8eaf0', boxShadow: 'none' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>Daily Subscriptions</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <LegendDot color={GOLD} label="Gold" />
          <LegendDot color={GREEN} label="Green" />
        </Box>
      </Box>

      {!hasData ? (
        <Box sx={{ height: PLOT_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">No subscriptions in this period</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex' }}>
          {/* Y axis */}
          <Box sx={{ width: 40, height: PLOT_HEIGHT, position: 'relative', flexShrink: 0 }}>
            {ticks.map((t) => (
              <Typography key={t} variant="caption" color="text.disabled" sx={{
                position: 'absolute', right: 8, top: `${(1 - t / niceMax) * 100}%`, transform: 'translateY(-50%)',
                fontSize: 11, whiteSpace: 'nowrap',
              }}>
                {t.toLocaleString()}
              </Typography>
            ))}
          </Box>

          {/* Plot */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ position: 'relative', height: PLOT_HEIGHT }}>
              {ticks.map((t) => (
                <Box key={t} sx={{
                  position: 'absolute', left: 0, right: 0, top: `${(1 - t / niceMax) * 100}%`,
                  borderTop: '1px solid #e8eaf0',
                }} />
              ))}
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end' }}>
                {days.map((d, idx) => {
                  const totalPx = (d.total / niceMax) * PLOT_HEIGHT;
                  const goldPx = d.total > 0 ? (d.gold / d.total) * totalPx : 0;
                  const greenPx = d.total > 0 ? (d.green / d.total) * totalPx : 0;
                  const posClass = idx < days.length * 0.15 ? 'start' : idx > days.length * 0.85 ? 'end' : 'center';
                  return (
                    <Box
                      key={d.date}
                      tabIndex={0}
                      role="button"
                      aria-label={`${fmtTooltipDate(d.date)}: ${d.gold} gold, ${d.green} green, ${d.total} total`}
                      sx={{
                        flex: 1, minWidth: 0, height: '100%', position: 'relative',
                        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                        outline: 'none', cursor: 'default',
                        '&:hover .bar-tooltip, &:focus-visible .bar-tooltip': { opacity: 1, visibility: 'visible' },
                        '&:hover .bar-fill, &:focus-visible .bar-fill': { filter: 'brightness(0.92)' },
                        '&:focus-visible': { boxShadow: 'inset 0 0 0 2px #6366f1', borderRadius: 1 },
                      }}
                    >
                      <DayTooltip day={d} position={posClass} />
                      <Box className="bar-fill" sx={{ width: '100%', maxWidth: 24, mx: 'auto', display: 'flex', flexDirection: 'column' }}>
                        {goldPx > 0 && (
                          <Box sx={{
                            height: `${goldPx}px`, bgcolor: GOLD, borderRadius: '4px 4px 0 0',
                            ...(greenPx > 0 ? { mb: '2px' } : {}),
                          }} />
                        )}
                        {greenPx > 0 && (
                          <Box sx={{ height: `${greenPx}px`, bgcolor: GREEN, borderRadius: goldPx > 0 ? 0 : '4px 4px 0 0' }} />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* X axis */}
            <Box sx={{ display: 'flex', mt: 1 }}>
              {days.map((d, idx) => {
                const showLabel = idx % labelInterval === 0 || idx === days.length - 1;
                let text = '';
                if (showLabel) {
                  const month = d.date.slice(0, 7);
                  text = fmtAxisDay(d.date, lastLabeledMonth !== month);
                  lastLabeledMonth = month;
                }
                return (
                  <Box key={d.date} sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                    {showLabel && (
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                        {text}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

// ─── Breakdown table ─────────────────────────────────────────────────────

function PlatformRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, pl: 6, pr: 2 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value.toLocaleString()}</Typography>
    </Box>
  );
}

function TierRow({ color, label, total, ios, android, other }) {
  const [open, setOpen] = useState(true);
  const toggle = () => setOpen((o) => !o);
  return (
    <Box>
      <Box
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          py: 1.25, px: 2, cursor: 'pointer',
          '&:hover': { bgcolor: '#fafafa' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {open ? <KeyboardArrowDownIcon fontSize="small" color="action" /> : <KeyboardArrowRightIcon fontSize="small" color="action" />}
          <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
          <Typography variant="body2" fontWeight={600}>{label}</Typography>
        </Box>
        <Typography variant="body2" fontWeight={700}>{total.toLocaleString()}</Typography>
      </Box>
      <Collapse in={open}>
        <PlatformRow label="iOS" value={ios} />
        <PlatformRow label="Android" value={android} />
        {other > 0 && <PlatformRow label="Other" value={other} />}
      </Collapse>
    </Box>
  );
}

function BreakdownTable({ summary, totals }) {
  const gold = summary.find((s) => s.productId === 'jamsap_gold_sub') || { total: 0, ios: 0, android: 0, other: 0 };
  const green = summary.find((s) => s.productId === 'jamsap_green_sub') || { total: 0, ios: 0, android: 0, other: 0 };
  return (
    <Paper sx={{ borderRadius: 2, border: '1px solid #e8eaf0', boxShadow: 'none', overflow: 'hidden' }}>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>Subscriptions by Platform</Typography>
      </Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.25, px: 2,
        borderTop: '1px solid #e8eaf0', borderBottom: '1px solid #e8eaf0', bgcolor: '#f8f9fc',
      }}>
        <Typography variant="body2" fontWeight={700}>Total</Typography>
        <Typography variant="body2" fontWeight={700}>{(totals?.total ?? 0).toLocaleString()}</Typography>
      </Box>
      <TierRow color={GOLD} label="Gold" total={gold.total} ios={gold.ios} android={gold.android} other={gold.other} />
      <Divider />
      <TierRow color={GREEN} label="Green" total={green.total} ios={green.ios} android={green.android} other={green.other} />
    </Paper>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────

export default function Analytics() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const [mode, setMode] = useState('month');
  const [selMonth, setSelMonth] = useState(now.getMonth());
  const [selYear, setSelYear] = useState(currentYear);

  const initialRange = monthRange(currentYear, now.getMonth());
  const [rangeStart, setRangeStart] = useState(initialRange.start);
  const [rangeEnd, setRangeEnd] = useState(initialRange.end);

  const { start, end } = mode === 'month' ? monthRange(selYear, selMonth) : { start: rangeStart, end: rangeEnd };
  const rangeInvalid = mode === 'range' && (!rangeStart || !rangeEnd || rangeStart > rangeEnd);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    if (rangeInvalid) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await getUsersAnalyticsTimeseriesApi({ start, end });
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load analytics.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end, rangeInvalid]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const shiftMonth = (delta) => {
    let m = selMonth + delta;
    let y = selYear;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setSelMonth(m);
    setSelYear(y);
  };
  const atCurrentMonth = selYear === currentYear && selMonth === now.getMonth();

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader title="Analytics" subtitle="Green & Gold subscriber activity — visible to super admins only" />

      <Paper sx={{
        p: 2, mb: 3, borderRadius: 2, border: '1px solid #e8eaf0', boxShadow: 'none',
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
      }}>
        <ToggleButtonGroup size="small" value={mode} exclusive onChange={(e, v) => v && setMode(v)}>
          <ToggleButton value="month" sx={{ textTransform: 'none', px: 2 }}>Month</ToggleButton>
          <ToggleButton value="range" sx={{ textTransform: 'none', px: 2 }}>Date range</ToggleButton>
        </ToggleButtonGroup>

        {mode === 'month' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <TextField select size="small" value={selMonth} onChange={(e) => setSelMonth(Number(e.target.value))} sx={{ minWidth: 130 }}>
              {MONTH_NAMES.map((m, i) => <MenuItem key={m} value={i}>{m}</MenuItem>)}
            </TextField>
            <TextField select size="small" value={selYear} onChange={(e) => setSelYear(Number(e.target.value))} sx={{ minWidth: 100 }}>
              {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
            <IconButton size="small" onClick={() => shiftMonth(1)} disabled={atCurrentMonth} aria-label="Next month">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Start" type="date" size="small" value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Typography color="text.secondary">–</Typography>
            <TextField
              label="End" type="date" size="small" value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
        )}

        {loading && <CircularProgress size={18} sx={{ ml: 'auto' }} />}
      </Paper>

      {rangeInvalid && <Alert severity="warning" sx={{ mb: 2 }}>Start date must be on or before the end date.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!data && loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : data && (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity .15s ease' }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <StatCard icon={<PeopleAltIcon />} label="Total Subscribers" value={data.totals.total.toLocaleString()} color="#6366f1" />
            <StatCard icon={<VerifiedIcon />} label="Green Subscribers" value={data.totals.green.toLocaleString()} color={GREEN} />
            <StatCard icon={<StarIcon />} label="Gold Subscribers" value={data.totals.gold.toLocaleString()} color={GOLD} />
          </Box>

          <Box sx={{ mb: 3 }}>
            <SubscriberBarChart days={data.days} />
          </Box>

          <BreakdownTable summary={data.summary} totals={data.totals} />
        </Box>
      )}
    </Box>
  );
}
